import { propertyType, t } from '@blocksuite/data-view';
import { Text } from '@blocksuite/store';
import zod from 'zod';

import { EditorHostKey } from '../../context/host-context.js';
import { isLinkedDoc } from '../../utils/title-doc.js';
import { textFromString, textHostDoc } from '../text-from-string.js';

export const titleColumnType = propertyType('title');

function attachTextIfDetached(value: Text): void {
  if (value.yText.doc) return;
  textHostDoc().getMap('root').set('text', value.yText);
}

export const titlePropertyModelConfig = titleColumnType.modelConfig({
  name: 'Title',
  propertyData: {
    schema: zod.object({}),
    default: () => ({}),
  },
  jsonValue: {
    schema: zod.string(),
    type: () => t.richText.instance(),
    isEmpty: ({ value }) => !value,
  },
  rawValue: {
    schema: zod.custom<Text>(data => data instanceof Text).optional(),
    default: () => undefined,
    toString: ({ value }) => value?.toString() ?? '',
    fromString: ({ value }) => {
      return { value: textFromString(value) };
    },
    toJson: ({ value, dataSource }) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      const host = dataSource.serviceGet(EditorHostKey);
      if (host) {
        const collection = host.std.workspace;
        const deltas = value.deltas$.value;
        const text = deltas
          .map(delta => {
            if (isLinkedDoc(delta)) {
              const linkedDocId = delta.attributes?.reference?.pageId as string;
              return collection.getDoc(linkedDocId)?.meta?.title;
            }
            return delta.insert;
          })
          .join('');
        return text;
      }
      return value?.toString() ?? '';
    },
    fromJson: ({ value }) => textFromString(value),
    onUpdate: ({ value, callback }) => {
      value?.yText.observe(callback);
      callback();
      return {
        dispose: () => {
          value?.yText.unobserve(callback);
        },
      };
    },
    setValue: ({ value, newValue }) => {
      if (value == null) {
        return;
      }
      const v = newValue as unknown;
      if (v == null) {
        value.replace(0, value.length, '');
        return;
      }
      if (typeof v === 'string') {
        value.replace(0, value.length, v);
        return;
      }
      if (newValue instanceof Text) {
        attachTextIfDetached(newValue);
        value.clear();
        value.applyDelta(newValue.toDelta());
        return;
      }
    },
  },
  fixed: {
    defaultData: {},
    defaultShow: true,
  },
});
